import React from "react";
import { useEffect } from "react";

import { css } from "@emotion/core";
import { isFunction, pick } from "lodash";

import { Alert, AlertDescription } from "@zapier/design-system";

import { Currency } from "@zapier/currency";
import { AsyncOverlay } from "@zapier/payment/esm/components/AsyncOverlay";
import { StripeProcessorType } from "@zapier/payment/esm/components/CreditCardForm/HostedFields/HostedFieldsProvider/processors/StripeProcessor";
import { usePaymentProcessor } from "@zapier/payment/esm/components/CreditCardForm/HostedFields/HostedFieldsProvider/usePaymentProcessor";
import { RetryableError } from "@zapier/payment/esm/components/PaymentApp/RetryableError";
import PaymentFormButtons from "@zapier/payment/esm/components/PaymentFormButtons";
import { FatalError, ValidationError } from "@zapier/payment/esm/errors";
import { SetupIntentConfirmationError } from "@zapier/payment/esm/errors/setup-intents";
import { useSetupIntentMachine } from "@zapier/payment/esm/hooks/useSetupIntentMachine";
import {
  ApiKeys,
  CreditCardBrandCode,
  CreditCardFormFields,
  FieldName,
  FieldValue,
  HostedFieldValue,
  PaymentDetailsCard,
  PaymentProcessor,
  PaymentSourceInput,
  PaymentSourceType,
} from "@zapier/payment/esm/types";
import useFormState from "@zapier/payment/esm/utils/useFormState";

import CreditCardErrors from "./CreditCardErrors";
import CreditCardFieldset from "./CreditCardFieldset";

type Props = {
  /** Payment processor API keys */
  apiKeys: ApiKeys;
  /** Payment processor to use */
  paymentProcessor: PaymentProcessor;
  /** Error message to show */
  error?: string | null;
  /** Show a cancel button */
  hasCancelButton?: boolean;
  /** User has saved payment source */
  hasPaymentSource: boolean;
  /** Editing is busy */
  isInFlight: boolean;
  /** Call to cancel current edit */
  onCancel: () => void;
  /** Called when card number changes */
  onCardNumberChange?: (brand?: CreditCardBrandCode | null) => void;
  /** Call when error happens during edit */
  onError: (error: Error) => void;
  /** Call to save current edit */
  onSave: (token: PaymentSourceInput) => void;
  /** Saved credit card details */
  savedCard: PaymentDetailsCard | undefined | null;
  /** What submethods of cards are supported? */
  supportedSubmethods: string[];
  /** The selected currency */
  selectedCurrency: Currency;
};

const formCss = css`
  display: grid;
  grid-gap: 10px;
`;

const flexCss = css`
  display: flex;
  justify-content: center;
`;

// Normalizing card brands that come from Stripe with what we represent them as.
const normalizeCardBrands = (cardBrand: string) => {
  switch (cardBrand) {
    case "amex":
      return "american-express";
    case "diners":
      return "diners-club";
    default:
      return cardBrand;
  }
};

const isPaymentMethodSupported = (
  { brand, isEmpty }: HostedFieldValue,
  supportedSubmethods: Props["supportedSubmethods"]
) =>
  Boolean(
    isEmpty ||
      !brand ||
      supportedSubmethods.includes(normalizeCardBrands(brand))
  );

const CreditCardForm: React.FC<Props> = props => {
  const {
    state: formState,
    updateErrors,
    checkIsValid,
    clearErrors,
    getAdditionalData,
    getFieldsErrors,
    startTokenization,
    stopTokenization,
    updateField,
  } = useFormState(PaymentSourceType.card, props.savedCard);

  const {
    paymentProcessor,
    apiKeys: { [paymentProcessor]: apiKey },
  } = props;

  const handleChange = (name: FieldName, value: FieldValue) => {
    // If changing card number state, trigger callback with card brand
    if (isFunction(props.onCardNumberChange) && name === "number") {
      props.onCardNumberChange(typeof value === "string" ? null : value.brand);
    }

    updateField(name, value);
  };

  const [
    stripeProcessor,
    processorInitializationError,
    initializeHostedFields,
  ] = usePaymentProcessor(paymentProcessor, apiKey as string, handleChange);

  const [state, send] = useSetupIntentMachine(paymentProcessor, {
    actions: {
      initializeHostedFields,
      destroyHostedFields: () => {
        // eslint-disable-next-line no-unused-expressions
        stripeProcessor?.destroyFields();
      },
    },
    services: {
      async savePaymentSource(context) {
        if (!context.setupIntent) {
          throw new FatalError(
            "We must have a setup intent in order to confirm"
          );
        }

        /**
         * Check if the form is valid before we try to do anything. Reject with an indicator of a validation error so the
         * state machine knows what to transition to.
         */
        if (checkIsValid() === false) {
          updateErrors();
          throw new ValidationError();
        }

        const details = pick(getAdditionalData(), [
          "name",
          "addressCity",
          "addressState",
          "addressZip",
          "addressCountry",
        ]);

        const result = await (
          stripeProcessor as StripeProcessorType
        ).confirmCardSetupIntent(context.setupIntent.clientSecret, {
          address: {
            city: details.addressCity,
            state: details.addressState,
            postal_code: details.addressZip,
            country: details.addressCountry,
          },
          name: details.name,
        });

        if (result.ok === false) {
          // Transition the state machine to an error state where the user can retry
          clearErrors();
          throw new SetupIntentConfirmationError(
            "Failed confirming setup intent",
            result.error
          );
        }

        const confirmedSetupIntent = result.value;

        if (!confirmedSetupIntent) {
          clearErrors();
          throw new FatalError(
            "We must have a setup intent in order to save the payment source"
          );
        }

        startTokenization();

        try {
          clearErrors();

          // Depending on the payment processor, method may or may not be present
          // Hardcode it here so we can use it for tracking
          props.onSave({
            // @ts-ignore - these types are all sorts of confusing, this needs some work to get it working correctly
            details,
            token: confirmedSetupIntent.payment_method ?? "",
            method: PaymentSourceType.card,
            paymentProcessor: props.paymentProcessor,
            setupIntentId: confirmedSetupIntent.id,
          });
        } catch (e) {
          clearErrors();
          //@ts-ignore
          props.onError(e);
        } finally {
          stopTokenization();
        }

        return true;
      },
    },
  });

  const shouldStartSetupIntents = state.nextEvents.includes("startSetup");

  useEffect(() => {
    if (shouldStartSetupIntents) {
      send("startSetup");
    }
  }, [shouldStartSetupIntents, send]);

  const isSubmethodSupported = isPaymentMethodSupported(
    (formState.fields as CreditCardFormFields).number.value,
    props.supportedSubmethods
  );

  useEffect(() => {
    /**
     * Update our currency field. This is purely for displaying a useful error when a user has an unsupported payment method.
     */
    updateField(
      "currency",
      `${props.selectedCurrency.name} ${props.selectedCurrency.symbol}`,
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedCurrency.name, props.selectedCurrency.symbol]);

  useEffect(() => {
    /**
     * Whenever we decide if we have a supported or unsupported payment method, update that in state so we can show a relevant error.
     */
    updateField(
      "isSubmethodSupported",
      isSubmethodSupported ? "supported" : "unsupported",
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmethodSupported]);

  const isBusy =
    props.isInFlight ||
    formState.isTokenizing ||
    state.matches("setup") ||
    state.matches("savePaymentSource");

  const hasServerError = !formState.isDirty && props.error;
  const hasFormError = formState.hasErrors;
  const submitText = `${props.hasPaymentSource ? "Update" : "Add"} card`;

  if (state.context.error)
    return (
      <div css={flexCss}>
        <RetryableError
          msg="We are sorry, there was an error setting up your payment method. Please try again or choose a different payment method."
          onCancel={props.onCancel}
          onRetry={() => send("retry")}
        />
      </div>
    );

  if (processorInitializationError) {
    return (
      <Alert title={processorInitializationError.title} type="error">
        <AlertDescription>
          {processorInitializationError.description}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <AsyncOverlay isVisible={isBusy}>
      <form
        css={formCss} // Used by E2E tests
        name="cc-form"
        onSubmit={e => {
          e.preventDefault();
          send("submit");
        }}
      >
        {hasServerError ? (
          <Alert type="error">{props.error}</Alert>
        ) : (
          hasFormError && <CreditCardErrors errors={getFieldsErrors()} />
        )}
        <CreditCardFieldset fields={formState.fields} onChange={handleChange} />
        <PaymentFormButtons
          buttonText={submitText}
          canSubmit={formState.isDirty}
          hasCancelButton={props.hasCancelButton}
          isBusy={isBusy}
          onCancel={() => {
            // Clear card type when credit card form is not shown
            if (props.onCardNumberChange) {
              props.onCardNumberChange(null);
            }

            props.onCancel();
          }}
        />
      </form>
    </AsyncOverlay>
  );
};

export type CreditCardFormProps = Props;
export default CreditCardForm;
